/**
 * NodeRenderer — Render nodes on canvas (via HTML overlay)
 * Per PLAN.md render/NodeRenderer.js
 * 
 * Nodes are rendered as HTML elements overlaid on the canvas
 * for proper KaTeX rendering. This class manages those elements.
 */

import { renderLatex } from '../latex/Renderer.js';

export class NodeRenderer {
    constructor() {
        this.container = null;
        this.nodeElements = new Map(); // nodeId → DOM element
    }

    /**
     * Initialize the node overlay container
     * @param {HTMLElement} parent 
     */
    init(parent) {
        this.container = document.createElement('div');
        this.container.id = 'node-overlay';
        this.container.style.cssText = `
      position: absolute;
      inset: 0;
      pointer-events: none;
      overflow: hidden;
    `;
        parent.appendChild(this.container);
    }

    /**
     * Render all nodes
     * @param {Node[]} nodes 
     * @param {import('../core/Viewport.js').Viewport} viewport 
     * @param {Set<string>} selectedIds 
     * @param {boolean} isDark 
     */
    render(nodes, viewport, selectedIds, isDark = false) {
        const existingIds = new Set(this.nodeElements.keys());
        const currentIds = new Set(nodes.map(n => n.id));

        // Remove deleted nodes
        for (const id of existingIds) {
            if (!currentIds.has(id)) {
                this.removeNodeElement(id);
            }
        }

        // Update or create nodes
        for (const node of nodes) {
            let el = this.nodeElements.get(node.id);

            if (!el) {
                el = this.createNodeElement(node, isDark);
                this.nodeElements.set(node.id, el);
            }

            this.updateNodeElement(el, node, viewport, selectedIds.has(node.id), isDark);
        }
    }

    /**
     * Create a DOM element for a node
     * @param {Node} node 
     * @param {boolean} isDark 
     * @returns {HTMLElement}
     */
    createNodeElement(node, isDark) {
        const el = document.createElement('div');
        el.className = `node ${node.type}`;
        el.dataset.nodeId = node.id;
        el.style.cssText = `
      position: absolute;
      pointer-events: auto;
      cursor: move;
      user-select: none;
      padding: 8px 16px;
      will-change: transform, left, top;
    `;

        this.container.appendChild(el);
        return el;
    }

    /**
     * Update a node element's position and content
     * @param {HTMLElement} el 
     * @param {Node} node 
     * @param {import('../core/Viewport.js').Viewport} viewport 
     * @param {boolean} isSelected 
     * @param {boolean} isDark 
     */
    updateNodeElement(el, node, viewport, isSelected, isDark) {
        const screen = viewport.worldToScreen(node.position.x, node.position.y);

        // Scale the node based on zoom
        el.style.left = `${screen.x}px`;
        el.style.top = `${screen.y}px`;
        el.style.transform = `scale(${viewport.zoom})`;
        el.style.transformOrigin = 'top left';

        // Selection state
        if (isSelected) {
            el.classList.add('selected');
        } else {
            el.classList.remove('selected');
        }

        // Update content if changed
        if (el.dataset.content !== node.content || el.dataset.type !== node.type) {
            el.dataset.content = node.content;
            el.dataset.type = node.type;

            if (node.type === 'latex') {
                el.innerHTML = renderLatex(node.content);
            } else {
                el.textContent = node.content;
            }

            // Measure and store size after render
            requestAnimationFrame(() => {
                const rect = el.getBoundingClientRect();
                node.setSize(rect.width / viewport.zoom, rect.height / viewport.zoom);
            });
        }
    }

    /**
     * Update only positions (fast path for panning/zooming)
     * @param {Node[]} nodes 
     * @param {import('../core/Viewport.js').Viewport} viewport 
     * @param {Set<string>} selectedIds 
     */
    updatePositions(nodes, viewport, selectedIds) {
        for (const node of nodes) {
            const el = this.nodeElements.get(node.id);
            if (!el) continue;

            const screen = viewport.worldToScreen(node.position.x, node.position.y);
            el.style.left = `${screen.x}px`;
            el.style.top = `${screen.y}px`;
            el.style.transform = `scale(${viewport.zoom})`;

            // Selection state
            if (selectedIds.has(node.id)) {
                el.classList.add('selected');
            } else {
                el.classList.remove('selected');
            }
        }
    }

    /**
     * Remove a node element
     * @param {string} nodeId 
     */
    removeNodeElement(nodeId) {
        const el = this.nodeElements.get(nodeId);
        if (el) {
            // Animate removal per PRD §10.4
            el.style.transition = 'opacity 100ms ease, transform 100ms ease';
            el.style.opacity = '0';
            el.style.transform = `${el.style.transform} scale(0.95)`;

            setTimeout(() => {
                el.remove();
            }, 100);

            this.nodeElements.delete(nodeId);
        }
    }

    /**
     * Get a node element by ID
     * @param {string} nodeId 
     * @returns {HTMLElement|undefined}
     */
    getElement(nodeId) {
        return this.nodeElements.get(nodeId);
    }

    /**
     * Clear all node elements
     */
    clear() {
        for (const [id, el] of this.nodeElements) {
            el.remove();
        }
        this.nodeElements.clear();
    }
}
